import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Droppable } from 'react-beautiful-dnd';
import { useDispatch } from 'react-redux';
import { Row } from 'react-table';
import { formatDuration, relativeFormatDuration } from '../date-utils';
import { useSelector } from '../hook-utils';
import { Agent, agentKeyToString, AgentLimt, CallState, Extension, ExtensionStatus, getAgentKey, getPresenceText, isQueueId, Media, Presence, QueueOrSquadId, QueueSquadType, sameIdentifier, UserLevel } from '../model';
import { actions } from '../store';
import { selectConfigProp, selectDateFormating, selectMyself, selectPresenceMap } from '../store/reducers/config';
import { ColumnWidths, getGridSortOrder, selectGridColumnWidths, selectShowQueueAgents } from '../store/reducers/localSettings';
import { selectDraggedMedia } from '../store/reducers/mediaDrag';
import { getNow } from '../store/reducers/now';
import { getSelectedExtension, selectQueueAgents, selectQueueAgentsAreLoading } from '../store/reducers/queueAgents';
import ExtensionIcon from './AgentIcon';
import ExtensionMenu from './AgentMenu';
import './AgentsTable.scss';
import { useCompactView } from './compactView';
import { FlexTable, Resizing, SortRules, TableColumn } from './FlexTable';
import { ReactComponent as WarningIcon } from './icons/warning.svg';
import { MediaItemIconType } from './MediaIcon';
import { MediaItem } from './MediaItem';
import { MediaMenuState } from './MediaMenu';
import { MenuTarget, targetFromEvent } from './Menu';
import MoreButton from './MoreButton';
import ProfilePhoto from './ProfilePhoto';

function getColumns(queue: QueueOrSquadId, onMore: (e: React.MouseEvent, item: Extension) => void, onMediaMenu: (state: MediaMenuState) => void, columnsWidths?: ColumnWidths): Array<TableColumn<Agent>> {
  let column: TableColumn<Agent>[] = [{
    id: 'presence',
    className: 'presence',
    accessor: i => <PresenceInfo extension={i} enabled={i.enabled} />,
    width: 42,
    disableResizing: true,
    sortType: (rowA: Row<Agent>, rowB: Row<Agent>) => {
      if ((rowA.original.status === ExtensionStatus.None || rowA.original.status === ExtensionStatus.Released) && (rowB.original.status === ExtensionStatus.None || rowB.original.status === ExtensionStatus.Released)) {
        const a = rowA.original.presence
        const b = rowB.original.presence
        if (a === b) return 0
        return (a > b) ? -1 : 1

      } else {
        const a = rowA.original.status
        const b = rowB.original.status
        if (a === b) return 0
        return (a > b) ? -1 : 1
      }
    },
  }, {
    id: 'extension',
    Header: 'Extension',
    accessor: a => a.id,
    width: 75,
  }, {
    id: 'agent',
    Header: 'Agent',
    accessor: i => <div className='agent' onClick={e => onMore(e, i)}>
      <AgentInfo agent={i} />
    </div>,
    width: 250,
    sortType: (rowA: Row<Agent>, rowB: Row<Agent>) => {
      const a = rowA.original.name.toLocaleLowerCase()
      const b = rowB.original.name.toLocaleLowerCase()
      if (a === b) return 0
      return (a > b) ? -1 : 1
    },
  }]

  if (queue.type === 'queue') {
    column = column.concat([{
      id: 'answered',
      Header: 'Answered',
      accessor: a => a.answered,
      className: 'center',
      headerClassName: 'center',
      width: 75,
    }, {
      id: 'avgTalk',
      Header: 'Avg Talk',
      accessor: i => {
        const x = i.answered ? i.talkTime / i.answered : 0
        return x && formatDuration(x)
      },
      className: 'center',
      headerClassName: 'center',
      width: 80,
    }])
  }

  column = column.concat(
    [{
      id: 'media',
      Header: 'Media',
      accessor: a => <MediaList agent={a} onMediaMenu={onMediaMenu} />,
      sortType: (rowA: Row<Agent>, rowB: Row<Agent>) => {
        const a = rowA.original.media.length
        const b = rowA.original.media.length
        if (a === b) return 0
        return (a > b) ? -1 : 1
      },
    }, {
      id: 'status',
      Header: 'Agent Status',
      className: 'status center',
      headerClassName: 'center',
      accessor: a => <AgentStatus media={a.media} />,
      width: 80,
      disableSortBy: true,
    }, {
      id: 'actions',
      accessor: q => <MoreButton className='menu-button' onClick={e => onMore(e, q)} />,
      width: 35,
      disableResizing: true,
      disableSortBy: true,
    }])

  return column.map(x => {
    if (x.id && columnsWidths && columnsWidths[x.id]) {
      return {
        ...x,
        width: columnsWidths[x.id],
      }
    }
    return x
  })
}

function AgentStatus({ media }: { media: Media[] }) {
  const agentLimits = useSelector(s => selectConfigProp(s, 'agentLimits'));
  const now = useSelector(getNow)
  const title = agentLimits && agentStatusTitle(now, agentLimits, media)

  return useMemo(() => {
    if (title === undefined || title === '') {
      return null
    }

    return <div title={title}><WarningIcon /></div>
  }, [title])
}

export function agentStatusTitle(now: number, agentLimits: AgentLimt[], media: Media[]): string {
  let title = ''
  let wrapupTime = false, heldTime = false, talkTime = false, totalTime = false
  for (const m of media) {
    if (m.status !== CallState.ExtensionPending) {
      //currently using CALL for VM
      const agentLimit = agentLimits.filter(l => l.type === m.mediaType || (m.mediaType === "VM" && l.type === "CALL"))[0]
      const elapsedTime = Math.floor((now - m.elapsedTime) / 1000)

      if (!wrapupTime && agentLimit.wrapupTime > 0 && (m.status === CallState.WaitingForWrapup ? elapsedTime : m.wrapupTime) > agentLimit.wrapupTime) {
        title += '\nWrapup time limit of \'' + agentLimit.wrapupTime + '\' seconds has been triggered'
        wrapupTime = true
      }

      if (!heldTime && agentLimit.heldTime > 0 && (m.status === CallState.QueueHold || m.status === CallState.Hold ? elapsedTime : m.heldTime) > agentLimit.heldTime) {
        title += '\nHeld time limit of \'' + agentLimit.heldTime + '\' seconds has been triggered'
        heldTime = true
      }

      if (!talkTime && agentLimit.talkTime > 0 && (m.status === CallState.ExtensionAnswered || m.status === CallState.ExtensionTransfer ? elapsedTime : m.talkTime) > agentLimit.talkTime) {
        title += '\nTalk time limit of \'' + agentLimit.talkTime + '\' seconds has been triggered'
        talkTime = true
      }

      if (agentLimit.totalTime > 0) {
        let total = 0
        if (m.status === CallState.WaitingForWrapup) {
          total += elapsedTime + m.heldTime + m.talkTime
        } else if (m.status === CallState.QueueHold || m.status === CallState.Hold) {
          total += m.wrapupTime + elapsedTime + m.talkTime
        } else if (m.status === CallState.ExtensionAnswered || m.status === CallState.ExtensionTransfer) {
          total += m.wrapupTime + m.heldTime + elapsedTime
        } else {
          total += m.wrapupTime + m.heldTime + m.talkTime
        }

        if (!totalTime && total > agentLimit.totalTime) {
          title += '\nTotal time limit of \'' + agentLimit.totalTime + '\' seconds has been triggered'
          totalTime = true
        }
      }
    }
  }
  return title;
}


type MenuState = { target: MenuTarget, item: Extension }

export default function AgentsTable({ id, server, type, onMediaMenu, maxTableHeight }: { id: string, server: string, type: QueueSquadType, onMediaMenu: (state: MediaMenuState) => void, maxTableHeight?: number }) {
  const dispatch = useDispatch()
  const gridType = 'agents'
  const widths = useSelector(s => selectGridColumnWidths(s, gridType))
  const refColumnResizeState = useRef<ColumnWidths>()

  const agents = useSelector(state => selectQueueAgents(state, { id, server, type }))
  const isLoading = useSelector(state => selectQueueAgentsAreLoading(state, { id, server, type }))
  const columns = useMemo(() => getColumns({ server, id, type }, onMore, onMediaMenu, widths), [server, id, type, onMediaMenu, widths])
  const showAgents = useSelector(s => selectShowQueueAgents(s, { id, server }))

  const compactView = useCompactView()
  const [menuState, setMenuState] = useState<MenuState>()
  const selected = useSelector(getSelectedExtension)
  const sorted = useSelector(s => getGridSortOrder(s, gridType))

  function onMore(e: React.MouseEvent, item: Extension) {
    e.preventDefault()
    e.stopPropagation()
    setMenuState({ target: targetFromEvent(e), item })
  }

  useEffect(() => {
    dispatch(actions.fetchQueueAgents.request({ id, server, type }))
  }, [dispatch, id, server, type])

  useEffect(() => {
    return () => {
      dispatch(actions.updateSelectedExtension(undefined))
      dispatch(actions.clearSelectedNumber())
    }
  }, [dispatch])

  const getTrProps = useCallback((agent: Agent): AgentRowProps => {
    return {
      queueId: { id, server, type },
      agent,
      onContextMenu(e) {
        setMenuState({ target: targetFromEvent(e), item: agent })
      },
      className: ((selected && agent.id === selected.id.id && agent.server === selected.id.server && selected.queue && selected.queue.id === id) || (menuState && agent.id === menuState.item.id && agent.server === menuState.item.server)) ? 'selected' : undefined,
      onClick() {
        dispatch(actions.setSelectedNumber(agent.id))
        dispatch(actions.updateSelectedExtension(agent, undefined, { id, server, type }))
      },
    }
  }, [dispatch, id, menuState, selected, server, type])

  const onSortBy = useCallback((sortBy: SortRules<Agent>) => {
    dispatch(actions.requestSetGridSortOrder(gridType, sortBy))
  }, [dispatch])

  const onColumnResize = useCallback((columnResize: Resizing) => {
    if (columnResize && columnResize.columnWidths) {
      for (const key in columnResize.columnWidths) {
        if (Object.prototype.hasOwnProperty.call(columnResize.columnWidths, key)) {
          if (refColumnResizeState.current) {
            refColumnResizeState.current = { ...refColumnResizeState.current, [key]: columnResize.columnWidths[key] }
          } else {
            refColumnResizeState.current = { [key]: columnResize.columnWidths[key] }
          }
        }
      }
    }
  }, [])

  useEffect(() => {
    // need to send this a destruction of queue page to save widths for next time
    return () => {
      if (refColumnResizeState.current) {
        dispatch(actions.requestSetGridColumnWidth(gridType, refColumnResizeState.current))
      }
      refColumnResizeState.current = undefined;
    }

  }, [dispatch])

  if (!showAgents) return null

  return <>
    <FlexTable className={classNames('AgentsTable', compactView && 'compact-view', !maxTableHeight || maxTableHeight > 1 && 'fixed')}
      data={agents.filter(agent => agent.enabled)}
      loading={isLoading}
      columns={columns}
      trComponent={AgentRow}
      getRowProps={getTrProps}
      sortBy={sorted}
      onSortBy={onSortBy}
      onColumnResize={onColumnResize}
      maxTableHeight={maxTableHeight}
    />
    {menuState && <ExtensionMenu {...menuState}
      extension={menuState.item}
      onClose={() => setMenuState(undefined)} />}
  </>
}

export const PresenceInfo = React.forwardRef<HTMLDivElement, { extension: Extension, enabled?: boolean } & React.HTMLAttributes<HTMLDivElement>>(({ extension, enabled, ...props }, ref) => {
  const format = useSelector(selectDateFormating)
  const presenceMap = useSelector(selectPresenceMap)
  const presence = getPresenceText(presenceMap, extension)
  const [ago, setAgo] = useState('')

  const updateAgo = useCallback(() => {
    if (extension.startTime === undefined || extension.startTime === null || format === null) {
      setAgo('')
      return
    }
    const sinceText = '\nSince: ' + formatReturnDateTime(extension.startTime, format.longDateFormat, format.shortTimeFormat) + '\n'
    setAgo(sinceText + 'Duration: ' + relativeFormatDuration(Math.ceil(dayjs(extension.startTime).diff(Date.now(), 's', true)) * -1))
  }, [extension.presence, extension.startTime])

  useEffect(() => updateAgo(), [updateAgo])

  if (format === null) return null

  const until = extension.returnTime ? ' until ' + formatReturnDateTime(extension.returnTime, format.longDateFormat, format.shortTimeFormat) : ''
  return <div {...props} title={presence + until + ago} onMouseEnter={() => updateAgo()}>
    <ExtensionIcon id={extension} enabled={enabled} />
  </div>
})

function AgentInfo({ agent }: { agent: Agent }) {
  const agentLimits = useSelector(s => selectConfigProp(s, 'agentLimits'));
  const now = useSelector(getNow)
  const format = useSelector(selectDateFormating)
  const presenceMap = useSelector(selectPresenceMap)

  if (!agentLimits || format === null) return null

  let title = agentStatusTitle(now, agentLimits, agent.media)

  return <>
    <ProfilePhoto className='photo' extension={agent} name={agent.name} datetime={agent.imageImageDate} />
    <div className='agentDetails'>
      <div className={classNames(title !== '' && 'warning')}>{agent.name}</div>
      <div className={classNames(title !== '' && 'warning', 'presence')}>
        {getPresenceText(presenceMap, agent)}
        {agent.returnTime && <span title={dayjs(agent.returnTime).format('LL LT')}>{' until ' + formatReturnDateTime(agent.returnTime, format.longDateFormat, format.shortTimeFormat)}</span>}
      </div>
    </div>
  </>
}

export function getUtcNow() {
  const now = new Date()
  const utc_timestamp = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(),
    now.getUTCHours(), now.getUTCMinutes(), now.getUTCSeconds(), now.getUTCMilliseconds());
  return dayjs(utc_timestamp).valueOf()
}

export function formatRelativeDateTime(time: number, dateFormat: string, timeFormat?: string, utc?: boolean) {
  let t = dayjs(time)
  if (utc) {
    t = t.utc()
  }
  return formatRelativeDayjsDateTime(t, dateFormat, timeFormat)
}

export function formatReturnDateTime(time: number, dateFormat: string, timeFormat?: string) {
  return formatRelativeDayjsDateTime(dayjs(time), dateFormat, timeFormat, true)
}

function formatRelativeDayjsDateTime(t: Dayjs, dateFormat: string, timeFormat?: string, midnightIsNoReturnTime?: boolean) {
  let time = timeFormat !== undefined ? ' ' + timeFormat : ''
  if (midnightIsNoReturnTime && t.hour() === 0 && t.minute() === 0 && t.second() === 0) {
    time = ''
  }

  if (t.isSame(Date.now(), 'd')) {
    return t.format('[Today]' + time)
  } else if (t.subtract(1, 'd').isSame(Date.now(), 'd')) {
    return t.format('[Tomorrow]' + time)
  } else if (t.subtract(-1, 'd').isSame(Date.now(), 'd')) {
    return t.format('[Yesterday]' + time)
  } else {
    return t.format(dateFormat + time)
  }
}

const DropContext = React.createContext<React.ReactElement<HTMLElement> | null>(null)

type AgentRowProps = React.HTMLAttributes<HTMLDivElement> & ({
  queueId: QueueOrSquadId
  agent: Agent
  className?: string
} | {})

function AgentRow({ children, ...props }: AgentRowProps) {
  const media = useSelector(selectDraggedMedia)
  const myId = useSelector(selectMyself)
  const userLevel = useSelector(s => selectConfigProp(s, 'baseUserLevel'))

  if (userLevel === undefined) return null

  if (!('queueId' in props)) {
    return <div {...props} className={classNames('rt-tr', props.className)} children={children} />
  }

  const { queueId, agent, ...rest } = props

  // other agents can only retrieve calls/vms
  const toMyself = myId && sameIdentifier(myId, { server: queueId.server, id: agent.id })
  const canRetrieve = media !== null && (toMyself || (media.mediaType === 'CALL' || media.mediaType === 'VM') || (userLevel === UserLevel.Supervisor || userLevel === UserLevel.Administrator || userLevel === UserLevel.LocalAdministrator))
  const alreadyHas = media !== null && agent.media.includes(media)

  return <Droppable isDropDisabled={!isQueueId(queueId) || !canRetrieve || alreadyHas}
    droppableId={agentKeyToString(getAgentKey(queueId, agent.id))} direction='horizontal'>
    {(provided, snapshot) => <DropContext.Provider value={provided.placeholder || null}>
      <div {...rest} className={classNames('rt-tr', snapshot.isDraggingOver && 'dragOver', props.className)} ref={provided.innerRef} {...provided.droppableProps}>
        {children}
      </div>
    </DropContext.Provider>}
  </Droppable>
}

interface MediaListProps {
  agent: Agent
  onMediaMenu(state: MediaMenuState): void
}

function MediaList({ agent, onMediaMenu }: MediaListProps) {
  const placeholder = useContext(DropContext)

  return <div className='media'>
    {placeholder}
    {agent.media.map((qi, i) => <MediaItem item={qi} key={qi.id} icon={MediaItemIconType.color} className='MediaItemAgent' onMenu={(e) => onMediaMenu({ target: targetFromEvent(e), items: [qi] })} />)}
  </div>
}
